import React, { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Smile, X } from 'lucide-react';
import { Button, Input } from './index';
import './ChatInput.css';

const ChatInput = ({ 
  onSendMessage, 
  onTyping, 
  disabled = false,
  placeholder = "Type your message...",
  showFileUpload = true,
  showEmoji = true,
  maxLength = 1000,
  className = ''
}) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim(), attachments);
      setMessage('');
      setAttachments([]);
      setIsTyping(false);
      onTyping?.(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleInputChange = (e) => {
    const newMessage = e.target.value;
    if (newMessage.length <= maxLength) {
      setMessage(newMessage);
      
      // Trigger typing indicator
      if (newMessage.trim() && !isTyping) {
        setIsTyping(true);
        onTyping?.(true);
      } else if (!newMessage.trim() && isTyping) {
        setIsTyping(false);
        onTyping?.(false);
      }
    }
  };

  const handleFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachments(prev => [...prev, ...files]);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleEmoji = () => {
    // Emoji picker functionality will be implemented in future phases
    // For now, just add a simple emoji
    setMessage(prev => prev + '😊');
  };

  return (
    <div className={`chat-input-container ${className}`}>
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="attachments-preview">
          {attachments.map((file, index) => (
            <div key={index} className="attachment-item">
              <span className="attachment-name">{file.name}</span>
              <Button
                type="button"
                onClick={() => removeAttachment(index)}
                variant="ghost"
                size="small"
                className="attachment-remove"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} className="chat-input-form">
        {/* File Upload Button */}
        {showFileUpload && (
          <Button
            type="button"
            onClick={handleFileUpload}
            disabled={disabled}
            variant="ghost"
            size="small"
            className="chat-input-button"
            title="Attach file"
          >
            <Paperclip className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.txt"
        />

        {/* Message Input */}
        <div className="chat-input-field">
          <Input
            ref={textareaRef}
            type="textarea"
            value={message}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            placeholder={placeholder}
            className="chat-input-textarea"
            rows={1}
            maxRows={4}
            maxLength={maxLength}
          />
          {maxLength && (
            <div className="character-count">
              {message.length}/{maxLength}
            </div>
          )}
        </div>

        {/* Emoji Button */}
        {showEmoji && (
          <Button
            type="button"
            onClick={handleEmoji}
            disabled={disabled}
            variant="ghost"
            size="small"
            className="chat-input-button"
            title="Add emoji"
          >
            <Smile className="h-4 w-4 md:h-5 md:w-5" />
          </Button>
        )}

        {/* Send Button */}
        <Button
          type="submit"
          disabled={!message.trim() || disabled}
          variant="primary"
          size="small"
          className="chat-input-send"
          title="Send message"
        >
          <Send className="h-4 w-4 md:h-5 md:w-5" />
        </Button>
      </form>
    </div>
  );
};

export default ChatInput;
